import {
  Children,
  cloneElement,
  isValidElement,
  useId,
  useState,
  type ReactElement,
  type ReactNode,
  type FocusEventHandler,
  type MouseEventHandler,
  type KeyboardEventHandler,
} from 'react';
import { cn } from '../../utils/cn';

export interface TooltipProps {
  readonly content: ReactNode;
  readonly children: ReactNode;
  readonly side?: 'top' | 'bottom' | 'start' | 'end';
  readonly className?: string;
}

const SIDE: Record<NonNullable<TooltipProps['side']>, string> = {
  top: 'bottom-full mb-1.5 start-1/2 -translate-x-1/2 rtl:translate-x-1/2',
  bottom: 'top-full mt-1.5 start-1/2 -translate-x-1/2 rtl:translate-x-1/2',
  start: 'end-full me-1.5 top-1/2 -translate-y-1/2',
  end: 'start-full ms-1.5 top-1/2 -translate-y-1/2',
};

/**
 * Hover/focus tooltip. Wraps a single child element and augments it with
 * aria-describedby. Pure CSS — no portal. For long-form rich content use
 * Popover instead.
 */
export function Tooltip({ content, children, side = 'top', className }: TooltipProps) {
  const id = useId();
  const [open, setOpen] = useState(false);

  type TriggerProps = {
    'aria-describedby'?: string;
    onMouseEnter?: MouseEventHandler;
    onMouseLeave?: MouseEventHandler;
    onFocus?: FocusEventHandler;
    onBlur?: FocusEventHandler;
    onKeyDown?: KeyboardEventHandler;
  };
  const arr = Children.toArray(children);
  let trigger: ReactNode = children;
  if (arr.length === 1 && isValidElement(arr[0])) {
    const child = arr[0] as ReactElement<TriggerProps>;
    const patch: TriggerProps = {
      onMouseEnter: (event) => {
        child.props.onMouseEnter?.(event);
        setOpen(true);
      },
      onMouseLeave: (event) => {
        child.props.onMouseLeave?.(event);
        setOpen(false);
      },
      onFocus: (event) => {
        child.props.onFocus?.(event);
        setOpen(true);
      },
      onBlur: (event) => {
        child.props.onBlur?.(event);
        setOpen(false);
      },
      onKeyDown: (event) => {
        child.props.onKeyDown?.(event);
        if (event.key === 'Escape') setOpen(false);
      },
    };
    if (open)
      patch['aria-describedby'] = [child.props['aria-describedby'], id].filter(Boolean).join(' ');
    trigger = cloneElement(child, patch);
  }

  return (
    <span className={cn('relative inline-block', className)}>
      {trigger}
      <span
        id={id}
        role="tooltip"
        hidden={!open}
        className={cn(
          'absolute z-50 pointer-events-none w-max max-w-[min(16rem,calc(100vw-2rem))] whitespace-normal',
          'bg-panel text-panel-ink text-[11.5px] font-medium px-2 py-1 rounded-md',
          'shadow-[0_4px_12px_-4px_rgba(0,0,0,0.10)]',
          SIDE[side],
        )}
      >
        {content}
      </span>
    </span>
  );
}
